import { ReactElement } from "react";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

export type WorkflowStepStatus =
  | "unselected"
  | "selected"
  | "completed"
  | "disabled";

export interface WorkflowStepProps {
  title: string;
  description: string;
  status?: WorkflowStepStatus;
  onClick?: () => void;
  className?: string;
}

const WorkflowStep = ({
  title,
  description,
  status = "unselected",
  onClick,
  className = "",
}: WorkflowStepProps): ReactElement => {
  const getStatusStyles = () => {
    switch (status) {
      case "unselected":
        return {
          container:
            "bg-gray-200 border border-gray-300 hover:bg-gray-100 cursor-pointer",
          icon: "flex items-center justify-center",
          title: "text-gray-800",
          description: "text-gray-600",
        };
      case "selected":
        return {
          container:
            "bg-brand-primary/20 border border-brand-primary hover:bg-brand-primary/30 cursor-pointer",
          icon: "flex items-center justify-center",
          title: "text-gray-800",
          description: "text-gray-600",
        };
      case "completed":
        return {
          container:
            "bg-success border border-success/50 hover:bg-success/80 cursor-pointer",
          icon: "flex items-center justify-center",
          title: "text-gray-800",
          description: "text-gray-600",
        };
      case "disabled":
        return {
          container:
            "bg-gray-100 border border-gray-200 cursor-not-allowed opacity-60",
          icon: "flex items-center justify-center",
          title: "text-gray-500",
          description: "text-gray-400",
        };
      default:
        return {
          container:
            "bg-gray-200 border border-gray-300 hover:bg-gray-100 cursor-pointer",
          icon: "flex items-center justify-center",
          title: "text-gray-800",
          description: "text-gray-600",
        };
    }
  };

  const getIconComponent = () => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="w-4 h-4" />;
      case "selected":
        return <RadioButtonUncheckedIcon className="w-4 h-4" />;
      default:
        return <RadioButtonUncheckedIcon className="w-4 h-4" />;
    }
  };

  const styles = getStatusStyles();
  const iconComponent = getIconComponent();

  return (
    <div
      className={`
        flex items-center gap-4 p-4 rounded-lg transition-all duration-200
        ${styles.container}
        ${className}
      `}
      onClick={status === "disabled" ? undefined : onClick}
    >
      {/* Left Icon */}
      <div className={styles.icon}>{iconComponent}</div>

      {/* Text Content */}
      <div className="flex-1">
        <Typography
          variant="h3"
          component="h3"
          className={`font-semibold ${styles.title}`}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          className={styles.description}
        >
          {description}
        </Typography>
      </div>

      {/* Right Arrow Icon */}
      {status !== "disabled" && (
        <div className="flex items-center justify-center">
          {status === "selected" || status === "completed" ? (
            <ArrowForwardIcon className="w-4 h-4 text-gray-800 rotate-180" />
          ) : (
            <ArrowForwardIcon className="w-4 h-4 text-gray-800" />
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowStep;
